<script lang="ts">
  // Simple sequential choropleth: one value per unit_id (e.g. early votes as % of registration).
  // Shapes + details UX come from MapCanvas (same hover / tap / keyboard behaviour as the results maps).
  import type { GeoCollection } from '../../lib/store';
  import { featureFor, featuresById, mapMix } from '../../lib/geo';
  import MapCanvas, { type MapItem } from './MapCanvas.svelte';
  let { geo, values, fmt, label, color = 'var(--c-turnout)', maxHeight = 460, empty = 'Nothing to show yet — this map fills in as results arrive.' }: { geo: GeoCollection; values: Map<string, number>; fmt: (v: number) => string; label: string; color?: string; maxHeight?: number; empty?: string } = $props();

  const STEPS = [18, 36, 54, 74, 94];
  const byId = $derived(featuresById(geo));
  const items = $derived<MapItem[]>([...byId.entries()]
    .filter(([k]) => !k.startsWith('~') && !k.startsWith('='))
    .map(([id, f]) => ({ id, f })));
  const nameOf = (id: string) => String(byId.get(id)?.properties?.name ?? id);
  // values are keyed by the data's unit_id; join them to shapes through the same fallbacks as the results maps
  // (case-insensitive ids, earlier spellings in alt_ids: 'Isle of Palms 1B', 'Town of Seabrook')
  const byShape = $derived.by(() => {
    const m = new Map<string, number>();
    for (const [k, v] of values) {
      const f = featureFor(byId, k);
      if (f && Number.isFinite(v)) m.set(String(f.properties.unit_id), v);
    }
    return m;
  });
  const vals = $derived([...byShape.values()].sort((a, b) => a - b));
  /** nothing to colour yet (e.g. before results every ratio is 0) */
  const isEmpty = $derived(!vals.length || vals[vals.length - 1]! <= 0);
  const qs = $derived([0.2, 0.4, 0.6, 0.8].map((q) => vals[Math.floor(q * (vals.length - 1))] ?? 0));
  const step = (v: number) => STEPS[qs.filter((q) => v > q).length]!;
  const fill = (id: string) => { const v = byShape.get(id); return v === undefined || isEmpty ? 'var(--map-empty)' : mapMix(color, step(v)); };
  const ariaFor = (id: string) => { const v = byShape.get(id); return `${nameOf(id)}: ${v === undefined || isEmpty ? 'no data' : fmt(v)}`; };
</script>

<div class="vm">
  <MapCanvas {items} {nameOf} {fill} {ariaFor} {label} {maxHeight} capToWidth={false}>
    {#snippet popup(id)}
      {@const v = byShape.get(id)}
      <div class="pt">{nameOf(id)}</div>
      <div class="pv">{v === undefined || isEmpty ? 'No data yet' : fmt(v)}</div>
      <div class="pl">{label}</div>
    {/snippet}
  </MapCanvas>
  {#if isEmpty}
    <div class="leg"><span class="muted">{empty}</span></div>
  {:else}
    <div class="leg"><span>{label}</span>{#each STEPS as s}<i style="background:{mapMix(color, s)}"></i>{/each}<span class="muted">{fmt(vals[0]!)} → {fmt(vals.at(-1)!)}</span></div>
  {/if}
</div>

<style>
  .pt { font: 700 1rem/1.2 var(--display); }
  .pv { font: 700 1.3rem/1.2 var(--display); margin-top: 4px; }
  .pl { color: var(--ink-3); font-size: 0.78rem; margin-top: 2px; }
  .leg { display: flex; align-items: center; gap: 4px; flex-wrap: wrap; font-size: 0.76rem; color: var(--ink-2); margin-top: 8px; }
  .leg span:first-child { font-weight: 700; color: var(--ink-3); margin-right: 6px; }
  .leg i { width: 18px; height: 10px; border-radius: 3px; }
  .leg .muted { margin-left: 6px; }
</style>
